/**
 * Instellingen (US-002, US-025, US-034, design-handoff scherm 10).
 *
 * Drie tabbladen: toegang & rollen, e-mailontvangers en de XML-feed. De feed-URL komt nooit
 * volledig terug van de server — je ziet uitsluitend een gemaskeerde weergave, en een nieuwe
 * URL wordt pas actief na een geslaagde verbindingstest.
 */

import { ROLE_LABELS, type Role } from '@prijsmonitor/shared';
import { useCallback, useEffect, useState } from 'react';

import { api, type ApiError } from '../api/client.js';
import { Button, Card, Chip, Notice, ui } from '../components/ui/index.js';
import { useSession } from '../session/SessionContext.js';
import styles from './SettingsScreen.module.css';

interface AccessUser {
  membershipId: string;
  userId: string;
  email: string;
  role: Role;
  lastLoginAt: string | null;
}

interface Recipient {
  id: string;
  email: string;
}

interface FeedStatus {
  configured: boolean;
  urlMasked: string | null;
  pendingUrlMasked: string | null;
  lastTestAt: string | null;
  lastTestOk: boolean;
  lastTestMessage: string | null;
  lastMatchCount: number | null;
}

/**
 * Een bedrijf zoals het bedrijvenbeheer het toont (US-003, US-038).
 *
 * Dit tabblad is er alleen voor de adressen in `PLATFORM_ADMIN_EMAILS`. Rechten zijn per bedrijf,
 * dus voor een nieuw bedrijf bestaat er nog geen beheerder die de eerste gebruiker kan toevoegen —
 * daar moet iets boven de bedrijven staan.
 */
interface Bedrijf {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  aantalGebruikers: number;
  aantalProducten: number;
  aantalBronnen: number;
  beheerders: string[];
}

type Tab = 'toegang' | 'ontvangers' | 'feed' | 'ai' | 'logo' | 'bedrijven';

export function SettingsScreen(): React.JSX.Element {
  // De sessie weet of dit bedrijf een logo heeft; na een wijziging moet die opnieuw worden gelezen
  // zodat de topbalk meteen klopt.
  const { activeCompany, refresh: herlaadSessie } = useSession();
  const [tab, setTab] = useState<Tab>('toegang');

  const [users, setUsers] = useState<AccessUser[]>([]);
  const [recipients, setRecipients] = useState<{ changes: Recipient[]; errors: Recipient[] }>({
    changes: [],
    errors: [],
  });
  const [feed, setFeed] = useState<FeedStatus | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<Role>('ANALYST');
  const [newRecipient, setNewRecipient] = useState({ changes: '', errors: '' });
  const [feedUrl, setFeedUrl] = useState('');
  const [aiHint, setAiHint] = useState('');

  /** Voor het verversen van het logo na een upload; de browser cachet hem op de ETag. */
  const [logoVersie, setLogoVersie] = useState(0);
  const [magBedrijven, setMagBedrijven] = useState(false);
  const [bedrijven, setBedrijven] = useState<Bedrijf[]>([]);
  const [nieuwBedrijf, setNieuwBedrijf] = useState('');

  const load = useCallback(async () => {
    try {
      const [u, r, f, a] = await Promise.all([
        api.get<{ users: AccessUser[] }>('/api/settings/users'),
        api.get<{ changes: Recipient[]; errors: Recipient[] }>('/api/settings/recipients'),
        api.get<FeedStatus>('/api/settings/feed'),
        api.get<{ aiSearchHint: string }>('/api/settings/ai'),
      ]);
      setUsers(u.users);
      setRecipients(r);
      setFeed(f);
      setAiHint(a.aiSearchHint);
    } catch (e) {
      setError((e as ApiError).message ?? 'De instellingen konden niet worden geladen.');
    }

    // Apart, en met opzet stil bij een fout: zonder platformrechten hoort dit tabblad niet te
    // bestaan, en dat is geen storing om over te melden.
    try {
      const toegang = await api.get<{ mag: boolean }>('/api/bedrijven/toegang');
      setMagBedrijven(toegang.mag);
      if (toegang.mag) {
        const lijst = await api.get<{ bedrijven: Bedrijf[] }>('/api/bedrijven');
        setBedrijven(lijst.bedrijven);
      }
    } catch {
      setMagBedrijven(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(key: string, action: () => Promise<string | void>): Promise<void> {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      const melding = await action();
      if (typeof melding === 'string') setNotice(melding);
      await load();
    } catch (e) {
      setError((e as ApiError).message ?? 'Er ging iets mis.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className={ui.page}>
      {error && <Notice tone="error">{error}</Notice>}
      {notice && <Notice tone="success">{notice}</Notice>}

      <div className={styles.tabs} role="tablist">
        {[
          { key: 'toegang', label: 'Toegang & rollen' },
          { key: 'ontvangers', label: 'E-mailontvangers' },
          { key: 'feed', label: 'XML-feed' },
          { key: 'ai', label: 'AI-zoekopdracht' },
          { key: 'logo', label: 'Logo' },
          ...(magBedrijven ? [{ key: 'bedrijven', label: 'Bedrijven' }] : []),
        ].map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={tab === item.key}
            className={tab === item.key ? styles.tabActive : styles.tab}
            onClick={() => setTab(item.key as Tab)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'toegang' && (
        <Card
          title="Toegang & rollen"
          description="Alleen adressen op deze lijst kunnen inloggen. Adressen worden hoofdletterongevoelig vergeleken; een alias van hetzelfde account telt niet automatisch mee."
        >
          <div className={ui.tableWrap}>
            <table className={ui.table}>
              <thead>
                <tr>
                  <th>E-mailadres</th>
                  <th>Rol</th>
                  <th>Laatst ingelogd</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.membershipId}>
                    <td>{user.email}</td>
                    <td>
                      <select
                        className={styles.inlineSelect}
                        value={user.role}
                        onChange={(e) =>
                          void run(user.membershipId, () =>
                            api
                              .patch(`/api/settings/users/${user.membershipId}`, {
                                role: e.target.value,
                              })
                              .then(() => 'Rol bijgewerkt.'),
                          )
                        }
                      >
                        <option value="ADMIN">{ROLE_LABELS.ADMIN}</option>
                        <option value="ANALYST">{ROLE_LABELS.ANALYST}</option>
                      </select>
                    </td>
                    <td>
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleDateString('nl-NL')
                        : 'nog nooit'}
                    </td>
                    <td>
                      <Button
                        variant="danger"
                        busy={busy === `del-${user.membershipId}`}
                        onClick={() =>
                          void run(`del-${user.membershipId}`, () =>
                            api
                              .delete(`/api/settings/users/${user.membershipId}`)
                              .then(() => 'Toegang ingetrokken.'),
                          )
                        }
                      >
                        Verwijderen
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.addRow}>
            <input
              className={styles.input}
              type="email"
              placeholder="naam@bedrijf.nl"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              aria-label="E-mailadres toevoegen"
            />
            <select
              className={styles.inlineSelect}
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as Role)}
              aria-label="Rol"
            >
              <option value="ANALYST">{ROLE_LABELS.ANALYST}</option>
              <option value="ADMIN">{ROLE_LABELS.ADMIN}</option>
            </select>
            <Button
              variant="secondary"
              busy={busy === 'add-user'}
              disabled={!newEmail.trim()}
              onClick={() =>
                void run('add-user', async () => {
                  await api.post('/api/settings/users', { email: newEmail, role: newRole });
                  setNewEmail('');
                  return 'Toegevoegd aan de toegangslijst.';
                })
              }
            >
              Toevoegen
            </Button>
          </div>
        </Card>
      )}

      {tab === 'ontvangers' && (
        <>
          {[
            {
              key: 'changes' as const,
              kind: 'PRICE_CHANGES',
              titel: 'Wijzigingsoverzicht',
              uitleg:
                'Ontvangt één e-mail per dag zodra minstens één prijs is gewijzigd sinds de laatst geldige meting.',
            },
            {
              key: 'errors' as const,
              kind: 'ERROR_SUMMARY',
              titel: 'Foutoverzicht',
              uitleg: 'Ontvangt één e-mail per dag zodra één of meer bronnen zijn mislukt.',
            },
          ].map((lijst) => (
            <Card key={lijst.key} title={lijst.titel} description={lijst.uitleg}>
              {recipients[lijst.key].length === 0 ? (
                <Notice tone="warn">
                  Er zijn nog geen ontvangers ingesteld. Resultaten worden wel opgeslagen, maar er
                  gaat geen e-mail uit.
                </Notice>
              ) : (
                <div className={styles.chips}>
                  {recipients[lijst.key].map((recipient) => (
                    <span key={recipient.id} className={styles.recipientChip}>
                      {recipient.email}
                      <button
                        type="button"
                        aria-label={`${recipient.email} verwijderen`}
                        onClick={() =>
                          void run(`del-${recipient.id}`, () =>
                            api
                              .delete(`/api/settings/recipients/${recipient.id}`)
                              .then(() => 'Ontvanger verwijderd.'),
                          )
                        }
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className={styles.addRow}>
                <input
                  className={styles.input}
                  type="email"
                  placeholder="naam@bedrijf.nl"
                  value={newRecipient[lijst.key]}
                  onChange={(e) =>
                    setNewRecipient((huidig) => ({ ...huidig, [lijst.key]: e.target.value }))
                  }
                  aria-label={`Ontvanger toevoegen aan ${lijst.titel}`}
                />
                <Button
                  variant="secondary"
                  busy={busy === `add-${lijst.key}`}
                  disabled={!newRecipient[lijst.key].trim()}
                  onClick={() =>
                    void run(`add-${lijst.key}`, async () => {
                      await api.post('/api/settings/recipients', {
                        email: newRecipient[lijst.key],
                        kind: lijst.kind,
                      });
                      setNewRecipient((huidig) => ({ ...huidig, [lijst.key]: '' }));
                      return 'Ontvanger toegevoegd.';
                    })
                  }
                >
                  Toevoegen
                </Button>
              </div>
            </Card>
          ))}
        </>
      )}

      {tab === 'ai' && (
        <Card
          title="Aanwijzing bij het zoeken naar concurrenten"
          description="Deze tekst gaat mee met elke zoekopdracht naar kandidaatpagina's. Gebruik hem om het zoeken te sturen: in welke branche u zit, welke soort winkels wel of juist niet meetellen."
        >
          <textarea
            className={styles.textarea}
            rows={6}
            value={aiHint}
            maxLength={2000}
            placeholder={
              'Bijvoorbeeld:\n' +
              'Wij verkopen kunststof wand- en plafondpanelen aan particulieren en aannemers.\n' +
              'Zoek bij gespecialiseerde bouwmaterialenwinkels, niet bij marktplaatsen of bol.com.\n' +
              'Panelen worden per stuk verkocht, niet per pak of per m².'
            }
            onChange={(e) => setAiHint(e.target.value)}
            aria-label="Aanwijzing voor de zoekopdracht"
          />

          <div className={styles.addRow}>
            <Button
              variant="secondary"
              busy={busy === 'ai'}
              onClick={() =>
                void run('ai', () =>
                  api
                    .put('/api/settings/ai', { aiSearchHint: aiHint })
                    .then(() => 'Aanwijzing opgeslagen.'),
                )
              }
            >
              Opslaan
            </Button>
          </div>

          <p className={ui.hint}>
            De harde eisen blijven altijd gelden en zijn hiermee niet te overschrijven: geen
            verzonnen adressen, één productpagina per winkel, en een afwijkende lengte telt niet
            mee. Elke kandidaat wordt daarna nog door de eigen crawler gecontroleerd.
          </p>
        </Card>
      )}

      {tab === 'feed' && feed && (
        <Card
          title="Sales Layer XML-feed"
          description="De feed levert onze eigen verkoopprijzen, gekoppeld op eigen artikelnummer. De URL bevat een token en wordt versleuteld bewaard; u ziet hem uitsluitend gemaskeerd."
        >
          <div className={styles.feedRow}>
            <span className={styles.feedLabel}>Actieve feed</span>
            <code className={styles.masked}>{feed.urlMasked ?? 'nog niet ingesteld'}</code>
            {feed.configured && (
              <Button
                busy={busy === 'hertest'}
                onClick={() =>
                  void run('hertest', () =>
                    api
                      .post<{ message: string }>('/api/settings/feed/hertest')
                      .then((r) => r.message),
                  )
                }
              >
                Test verbinding
              </Button>
            )}
          </div>

          {feed.lastTestAt && (
            <Notice tone={feed.lastTestOk ? 'success' : 'error'}>
              Laatste test op {new Date(feed.lastTestAt).toLocaleString('nl-NL')}:{' '}
              {feed.lastTestMessage}
            </Notice>
          )}

          {feed.pendingUrlMasked && (
            <div className={styles.pending}>
              <Chip tone="warn">Nog niet actief</Chip>
              <code className={styles.masked}>{feed.pendingUrlMasked}</code>
              <Button
                variant="primary"
                busy={busy === 'test'}
                onClick={() =>
                  void run('test', () =>
                    api
                      .post<{ ok: boolean; message: string }>('/api/settings/feed/test')
                      .then((r) => r.message),
                  )
                }
              >
                Testen en activeren
              </Button>
            </div>
          )}

          <div className={styles.addRow}>
            <input
              className={styles.input}
              type="url"
              placeholder="https://…/feed.xml?token=…"
              value={feedUrl}
              onChange={(e) => setFeedUrl(e.target.value)}
              aria-label="Nieuwe feed-URL"
            />
            <Button
              variant="secondary"
              busy={busy === 'feed'}
              disabled={!feedUrl.trim()}
              onClick={() =>
                void run('feed', async () => {
                  await api.post('/api/settings/feed', { url: feedUrl });
                  setFeedUrl('');
                  return 'De URL is vastgelegd. Test de verbinding om hem actief te maken.';
                })
              }
            >
              Opslaan
            </Button>
          </div>

          <p className={ui.hint}>
            Een nieuwe URL wordt pas actief nadat de verbindingstest is geslaagd. Tot die tijd
            blijft de huidige feed in gebruik.
          </p>
        </Card>
      )}

      {tab === 'logo' && (
        <Card
          title="Logo van dit bedrijf"
          description="Staat rechtsboven in plaats van de bedrijfsnaam. Bij meerdere bedrijven is een logo sneller te herkennen dan tekst."
        >
          <div className={styles.logoRij}>
            <div className={styles.logoVoorbeeld}>
              {activeCompany?.heeftLogo ? (
                <img
                  src={`/api/bedrijven/${activeCompany.companyId}/logo?v=${logoVersie}`}
                  alt={activeCompany.companyName}
                  className={styles.logoAfbeelding}
                />
              ) : (
                <span className={ui.hint}>Nog geen logo — rechtsboven staat de naam.</span>
              )}
            </div>

            <div className={styles.logoActies}>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                aria-label="Logo kiezen"
                onChange={(e) => {
                  const bestand = e.target.files?.[0];
                  e.target.value = '';
                  if (!bestand) return;
                  void run('logo', async () => {
                    const formulier = new FormData();
                    formulier.append('bestand', bestand);
                    const response = await fetch('/api/bedrijf/logo', {
                      method: 'POST',
                      credentials: 'same-origin',
                      body: formulier,
                    });
                    const uitkomst = (await response.json()) as {
                      melding?: string;
                      error?: { message?: string };
                    };
                    if (!response.ok) {
                      throw new Error(
                        uitkomst.error?.message ?? 'Het logo kon niet worden opgeslagen.',
                      );
                    }
                    setLogoVersie((v) => v + 1);
                    await herlaadSessie();
                    return uitkomst.melding ?? 'Het logo is opgeslagen.';
                  });
                }}
              />

              {activeCompany?.heeftLogo && (
                <Button
                  variant="danger"
                  busy={busy === 'logo-weg'}
                  onClick={() =>
                    void run('logo-weg', async () => {
                      const uitkomst = await api.delete<{ melding: string }>('/api/bedrijf/logo');
                      setLogoVersie((v) => v + 1);
                      await herlaadSessie();
                      return uitkomst.melding;
                    })
                  }
                >
                  Logo verwijderen
                </Button>
              )}
            </div>
          </div>

          <p className={ui.hint} style={{ marginTop: 12 }}>
            PNG, JPEG of WebP, hoogstens 512 kB. Een liggend logo werkt het best: de hoogte wordt op
            26 px gezet en de breedte volgt.
          </p>
          <p className={ui.hint}>
            SVG wordt bewust niet geaccepteerd. Daar kan script in staan, en een geüpload bestand
            dat we vanaf onze eigen omgeving serveren mag geen code kunnen worden. Het bestandstype
            wordt aan de inhoud herkend, niet aan wat de browser erover zegt.
          </p>
        </Card>
      )}

      {tab === 'bedrijven' && magBedrijven && (
        <Card
          title="Bedrijven"
          description="Elk bedrijf heeft zijn eigen producten, prijsbronnen, feed en gebruikers. Rechten gelden per bedrijf: beheerder bij het ene bedrijf zegt niets over het andere."
        >
          <div className={styles.addRow}>
            <input
              className={styles.input}
              placeholder="Naam van het nieuwe bedrijf, bijv. RVS Paleis"
              value={nieuwBedrijf}
              onChange={(e) => setNieuwBedrijf(e.target.value)}
              aria-label="Naam van het nieuwe bedrijf"
            />
            <Button
              busy={busy === 'bedrijf-toevoegen'}
              disabled={nieuwBedrijf.trim().length < 2}
              onClick={() =>
                void run('bedrijf-toevoegen', async () => {
                  const gemaakt = await api.post<{ melding: string }>('/api/bedrijven', {
                    name: nieuwBedrijf.trim(),
                  });
                  setNieuwBedrijf('');
                  return gemaakt.melding;
                })
              }
            >
              Bedrijf toevoegen
            </Button>
          </div>

          <div className={ui.tableWrap}>
            <table className={ui.table}>
              <thead>
                <tr>
                  <th>Bedrijf</th>
                  <th>Sleutel</th>
                  <th>Gebruikers</th>
                  <th>Producten</th>
                  <th>Beheerders</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {bedrijven.map((bedrijf) => (
                  <tr key={bedrijf.id}>
                    <td>
                      <input
                        className={styles.input}
                        defaultValue={bedrijf.name}
                        key={`naam-${bedrijf.id}-${bedrijf.name}`}
                        aria-label={`Naam van ${bedrijf.name}`}
                        onBlur={(e) => {
                          const waarde = e.target.value.trim();
                          if (waarde && waarde !== bedrijf.name) {
                            void run('bedrijf-naam', async () => {
                              await api.patch(`/api/bedrijven/${bedrijf.id}`, { name: waarde });
                              return `${bedrijf.name} heet nu ${waarde}.`;
                            });
                          }
                        }}
                      />
                    </td>
                    <td>
                      <code className={styles.masked}>{bedrijf.slug}</code>
                    </td>
                    <td>{bedrijf.aantalGebruikers}</td>
                    <td>{bedrijf.aantalProducten}</td>
                    <td>
                      {bedrijf.beheerders.length > 0 ? (
                        bedrijf.beheerders.join(', ')
                      ) : (
                        <Chip tone="warn">geen beheerder</Chip>
                      )}
                    </td>
                    <td>
                      <div className={styles.bedrijfActies}>
                        <Chip tone={bedrijf.active ? 'success' : 'muted'}>
                          {bedrijf.active ? 'Actief' : 'Inactief'}
                        </Chip>
                        <Button
                          variant="secondary"
                          onClick={() =>
                            void run('bedrijf-beheerder', async () => {
                              const email = window.prompt(
                                `Wie wordt beheerder van ${bedrijf.name}?\n\n` +
                                  'Het adres krijgt beheerrechten voor dit bedrijf. Bestaat het nog ' +
                                  'niet, dan wordt het aangemaakt.',
                              );
                              if (!email?.trim()) return;
                              const uitkomst = await api.post<{ melding: string }>(
                                `/api/bedrijven/${bedrijf.id}/beheerder`,
                                { email: email.trim() },
                              );
                              return uitkomst.melding;
                            })
                          }
                        >
                          Beheerder
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() =>
                            void run('bedrijf-actief', async () => {
                              if (bedrijf.active) {
                                const bevestigd = window.confirm(
                                  `${bedrijf.name} op inactief zetten?\n\n` +
                                    'Niemand kan er daarna nog in; lopende sessies voor dit bedrijf ' +
                                    'worden beëindigd. De gegevens blijven bewaard.',
                                );
                                if (!bevestigd) return;
                              }
                              await api.patch(`/api/bedrijven/${bedrijf.id}`, {
                                active: !bedrijf.active,
                              });
                              return `${bedrijf.name} staat nu ${bedrijf.active ? 'inactief' : 'actief'}.`;
                            })
                          }
                        >
                          {bedrijf.active ? 'Op inactief' : 'Activeren'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className={ui.hint}>
            Wie een bedrijf aanmaakt, wordt er meteen beheerder van. Wissel daarna van bedrijf via
            de bedrijfsnaam links bovenin en beheer de gebruikers en rollen bij “Toegang &amp;
            rollen” — die lijst geldt per bedrijf. Wilt u zelf geen lid blijven, wijs dan eerst een
            andere beheerder aan.
            <br />
            Dit tabblad is voorbehouden aan de adressen in PLATFORM_ADMIN_EMAILS op de server. Wie
            bedrijven mag aanmaken kan zichzelf overal beheerder maken, en dat recht hoort niet
            binnen de applicatie uitgedeeld te kunnen worden.
          </p>
        </Card>
      )}
    </div>
  );
}
